import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ChevronLeft, ChevronRight, Pause, Play, AlertTriangle, Clock, Calendar, Package } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useModuleAuth } from "@/hooks/useModuleAuth";
import { AuthCheckSkeleton } from "@/components/AuthCheckSkeleton";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPDDisplay = () => {
  const navigate = useNavigate();
  // Pro-only per-module auth wall (no-op on Core; FeatureGate already hides this on Core).
  const checking = useModuleAuth({ role: "spd", loginPath: "/spd-login" });
  const [orders, setOrders] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every second for live countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch pending orders
  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    try {
      // Fetch orders and product specs in parallel
      const [ordersRes, specsRes] = await Promise.all([
        fetch(`${API_URL}/api/spd/orders`),
        fetch(`${API_URL}/api/product-specifications`)
      ]);
      
      if (ordersRes.ok) {
        const data = await ordersRes.json();
        let specs = [];
        if (specsRes.ok) {
          specs = await specsRes.json();
        }
        
        // Build a map of product_id/item_name -> die_ids
        const specMap = {};
        for (const spec of specs) {
          const dieIds = spec.die_ids?.length > 0 ? spec.die_ids : (spec.die_id ? [spec.die_id] : []);
          if (dieIds.length > 0) {
            if (spec.product_id) specMap[spec.product_id] = dieIds;
            if (spec.item_name) specMap[spec.item_name] = dieIds;
          }
        }
        
        const ordersWithDates = (data.orders || [])
          .filter(order => 
            order.scheduled_datetime && 
            !["Completed", "Rejected", "Revoked", "Delivered"].includes(order.status)
          )
          .map(order => ({
            ...order,
            die_ids: specMap[order.product_id] || specMap[order.item_name] || []
          }))
          .sort((a, b) => new Date(a.scheduled_datetime) - new Date(b.scheduled_datetime));
        setOrders(ordersWithDates);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-slide functionality
  useEffect(() => {
    if (!isAutoPlaying || orders.length <= 1) return;
    
    const slideInterval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % orders.length);
    }, 8000); // Change every 8 seconds
    
    return () => clearInterval(slideInterval);
  }, [isAutoPlaying, orders.length]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex(prev => (prev - 1 + orders.length) % orders.length);
  }, [orders.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex(prev => (prev + 1) % orders.length);
  }, [orders.length]);

  // Calculate time difference and status
  const getOrderStatus = (dueDate) => {
    const due = new Date(dueDate);
    const now = currentTime;
    const diffMs = due - now;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((Math.abs(diffMs) % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((Math.abs(diffMs) % (1000 * 60 * 60)) / (1000 * 60));
    const diffSeconds = Math.floor((Math.abs(diffMs) % (1000 * 60)) / 1000);

    if (diffMs < 0) {
      // Overdue
      const overdueDays = Math.abs(diffDays) - 1;
      return {
        status: "OVERDUE",
        statusColor: "text-red-500",
        bgColor: "from-red-900/40 to-red-950/60",
        borderColor: "border-red-500/60",
        glowColor: "shadow-red-500/40",
        countdown: `-${Math.abs(diffDays)}:${String(diffHours).padStart(2, '0')}:${String(diffMinutes).padStart(2, '0')}:${String(diffSeconds).padStart(2, '0')}`,
        message: `OVERDUE BY: ${overdueDays > 0 ? overdueDays + ' Days ' : ''}${diffHours}:${String(diffMinutes).padStart(2, '0')}:${String(diffSeconds).padStart(2, '0')} Hours`,
        icon: AlertTriangle
      };
    } else if (diffDays === 0) {
      // Due Today
      return {
        status: "DUE TODAY",
        statusColor: "text-orange-400",
        bgColor: "from-orange-900/40 to-orange-950/60",
        borderColor: "border-orange-500/60",
        glowColor: "shadow-orange-500/40",
        countdown: `${String(diffHours).padStart(2, '0')}:${String(diffMinutes).padStart(2, '0')}:${String(diffSeconds).padStart(2, '0')}`,
        message: `DUE IN: ${diffHours}:${String(diffMinutes).padStart(2, '0')}:${String(diffSeconds).padStart(2, '0')} Hours`,
        icon: Clock
      };
    } else if (diffDays <= 3) {
      // Due Soon (within 3 days)
      return {
        status: "DUE SOON",
        statusColor: "text-yellow-400",
        bgColor: "from-yellow-900/40 to-yellow-950/60",
        borderColor: "border-yellow-500/60",
        glowColor: "shadow-yellow-500/40",
        countdown: `${diffDays}:${String(diffHours).padStart(2, '0')}:${String(diffMinutes).padStart(2, '0')}:${String(diffSeconds).padStart(2, '0')}`,
        message: `DUE IN: ${diffDays} Days ${diffHours}:${String(diffMinutes).padStart(2, '0')} Hours`,
        icon: Calendar
      };
    } else {
      // On Track
      return {
        status: "ON TRACK",
        statusColor: "text-emerald-400",
        bgColor: "from-emerald-900/40 to-emerald-950/60",
        borderColor: "border-emerald-500/60",
        glowColor: "shadow-emerald-500/40",
        countdown: `${diffDays}:${String(diffHours).padStart(2, '0')}:${String(diffMinutes).padStart(2, '0')}:${String(diffSeconds).padStart(2, '0')}`,
        message: `DUE IN: ${diffDays} Days ${diffHours} Hours`,
        icon: Package
      };
    }
  };

  const currentOrder = orders[currentIndex];

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  };

  if (checking) return <AuthCheckSkeleton />;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "radial-gradient(ellipse at center, #0A1910 0%, #000000 70%)" }}>
      {/* Back Button - Top Right */}
      <div className="absolute top-4 right-4 z-20">
        <Button
          onClick={() => navigate("/")}
          variant="outline"
          size="sm"
          className="border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Main Display Area */}
      <div className="flex-1 flex items-center justify-center px-6 py-8">
        {loading ? (
          <div className="text-gray-400 text-xl">Loading...</div>
        ) : orders.length === 0 ? (
          <Card className="bg-black/60 border-2 border-gray-700 p-12 text-center max-w-lg">
            <Package className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-400 mb-2">No Pending Orders</h2>
            <p className="text-gray-500">All deliveries are on track or completed!</p>
          </Card>
        ) : currentOrder ? (
          <div className="w-full max-w-4xl">
            {(() => {
              const orderStatus = getOrderStatus(currentOrder.scheduled_datetime);
              const StatusIcon = orderStatus.icon;
              const hasQRCode = currentOrder.image_drive_url;
              
              return (
                <Card className={`bg-gradient-to-br ${orderStatus.bgColor} border-2 ${orderStatus.borderColor} shadow-2xl ${orderStatus.glowColor} p-8 md:p-10 relative overflow-hidden`}>
                  {/* Background Pattern */}
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0" style={{ 
                      backgroundImage: `radial-gradient(circle at 2px 2px, currentColor 1px, transparent 0)`,
                      backgroundSize: '24px 24px'
                    }}></div>
                  </div>
                  
                  <div className="relative z-10">
                    {/* Top Row: Company & Status Badge */}
                    <div className="flex justify-between items-center mb-4">
                      {currentOrder.company_name && (
                        <span className="px-4 py-1.5 bg-black/50 rounded-full text-gray-300 text-sm font-medium">
                          {currentOrder.company_name}
                        </span>
                      )}
                      <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full ${orderStatus.statusColor} bg-black/50 font-bold text-sm`}>
                        <StatusIcon className="h-4 w-4" />
                        {orderStatus.status}
                      </div>
                    </div>
                    
                    {/* Main Content Row */}
                    <div className={`flex ${hasQRCode ? 'gap-8' : ''}`}>
                      {/* Left Side - Order Details */}
                      <div className={`${hasQRCode ? 'flex-1' : 'w-full'}`}>
                        {/* Item Name */}
                        <h2 className={`text-3xl md:text-4xl lg:text-5xl font-black text-white mb-4 tracking-wide ${hasQRCode ? '' : 'text-center'}`}>
                          {currentOrder.item_name}
                        </h2>
                        
                        {/* Quantity & PO Number */}
                        <div className={`flex gap-8 mb-4 ${hasQRCode ? '' : 'justify-center'}`}>
                          <div>
                            <p className="text-gray-400 text-xs uppercase tracking-wider">Quantity</p>
                            <p className="text-2xl md:text-3xl font-bold text-white">{currentOrder.total_quantity.toLocaleString()}</p>
                          </div>
                          {currentOrder.purchase_order_number && (
                            <div>
                              <p className="text-gray-400 text-xs uppercase tracking-wider">PO Number</p>
                              <p className="text-2xl md:text-3xl font-bold text-white">{currentOrder.purchase_order_number}</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Die IDs */}
                        {currentOrder.die_ids?.length > 0 && (
                          <div className={`mb-4 ${hasQRCode ? '' : 'text-center'}`}>
                            <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Die ID</p>
                            <div className={`flex flex-wrap gap-2 ${hasQRCode ? '' : 'justify-center'}`}>
                              {currentOrder.die_ids.map((dieId, idx) => (
                                <span key={dieId || idx} className="px-3 py-1 bg-cyan-500/20 border border-cyan-500/40 rounded-full text-cyan-300 text-sm font-bold">
                                  {dieId}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Delivery Date */}
                        <p className={`text-lg ${orderStatus.statusColor} font-semibold mb-4 ${hasQRCode ? '' : 'text-center'}`}>
                          Delivery: {formatDate(currentOrder.scheduled_datetime)}
                        </p>
                        
                        {/* Countdown Timer */}
                        <div className={`mb-3 ${hasQRCode ? '' : 'text-center'}`}>
                          <p className={`text-5xl md:text-6xl lg:text-7xl font-mono font-black ${orderStatus.statusColor} tracking-wider`}>
                            {orderStatus.countdown}
                          </p>
                          <p className="text-gray-500 text-sm mt-2 uppercase tracking-wide">
                            Days : Hours : Min : Sec
                          </p>
                        </div>
                        
                        {/* Status Message */}
                        <p className={`text-base text-gray-300 font-medium ${hasQRCode ? '' : 'text-center'}`}>
                          {orderStatus.message}
                        </p>
                      </div>
                      
                      {/* Right Side - QR Code (if exists) */}
                      {hasQRCode && (
                        <div className="flex-shrink-0 flex flex-col items-center justify-center">
                          <div className="bg-white p-3 rounded-xl shadow-lg">
                            <QRCodeSVG 
                              value={currentOrder.image_drive_url} 
                              size={140}
                              level="M"
                            />
                          </div>
                          <p className="text-gray-400 text-sm mt-3 text-center">Scan for Image</p>
                        </div>
                      )}
                    </div>
                    
                    {/* Note if exists */}
                    {currentOrder.note && (
                      <div className="mt-5 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                        <p className="text-yellow-300 text-center font-medium">
                          📝 {currentOrder.note}
                        </p>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })()}
          </div>
        ) : null}
      </div>

      {/* Navigation Controls */}
      {orders.length > 0 && (
        <div className="pb-8">
          <div className="flex items-center justify-center gap-4">
            <Button
              onClick={goToPrevious}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
              disabled={orders.length <= 1}
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              Previous
            </Button>
            
            <Button
              onClick={() => setIsAutoPlaying(!isAutoPlaying)}
              variant="outline"
              className={`border-gray-700 ${isAutoPlaying ? 'text-yellow-400 border-yellow-500/50' : 'text-gray-300'} hover:bg-gray-800`}
            >
              {isAutoPlaying ? (
                <>
                  <Pause className="h-5 w-5 mr-2" />
                  Pause
                </>
              ) : (
                <>
                  <Play className="h-5 w-5 mr-2" />
                  Play
                </>
              )}
            </Button>
            
            <Button
              onClick={goToNext}
              variant="outline"
              className="border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
              disabled={orders.length <= 1}
            >
              Next
              <ChevronRight className="h-5 w-5 ml-1" />
            </Button>
          </div>
          
          {/* Page Indicators */}
          {orders.length > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              {orders.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    idx === currentIndex 
                      ? 'bg-blue-500 w-6' 
                      : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                />
              ))}
            </div>
          )}
          
          {/* Order Counter */}
          <p className="text-center text-gray-500 text-sm mt-3">
            Showing {currentIndex + 1} of {orders.length} pending orders
          </p>
        </div>
      )}
    </div>
  );
};

export default SPDDisplay;
